import { Component } from '@theme/component';
import { closest, clamp, center, getVisibleElements } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { Scroller } from '@theme/scrolling';
import { cycleFocus } from '@theme/focus';

/**
 * Collection links component
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} container
 * @property {HTMLElement[]} [images]
 * @property {HTMLElement[]} [links]
 * @property {import('slideshow').Slideshow} slideshow
 * @property {import('slideshow').Slideshow} [mobileSlideshow]
 *
 * @extends {Component<Refs>}
 */
class CollectionLinks extends Component {
  requiredRefs = ['container'];

  /** @type {Scroller} */
  #scroll;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);

    this.#scroll = new Scroller(this.refs.container, { onScroll: this.#handleScroll });
    
    // Initialize descriptions visibility
    this.#updateDescriptions(this.currentIndex);
    
    // Initialize mobile slideshow if it exists
    if (this.refs.mobileSlideshow) {
      this.refs.mobileSlideshow.select(this.currentIndex, undefined, { animate: false });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scroll.destroy();
  }

  get links() {
    return this.refs.links || [];
  }

  get currentIndex() {
    return this.links.findIndex((link) => link.getAttribute('aria-current') === 'true');
  }

  /**
   * Public method to select a collection link
   *
   * @param {number} targetIndex
   * @param {PointerEvent} [event]
   */
  select(targetIndex, event) {
    this.#updateSelectedLink(targetIndex);
    this.refs.slideshow?.select(targetIndex, undefined, { animate: false });
    this.refs.mobileSlideshow?.select(targetIndex, undefined, { animate: false });
    this.#updateDescriptions(targetIndex);
    if (event) this.#revealImage(event);
  }

  /**
   * Public method to hover over a collection link (changes content but doesn't make active)
   *
   * @param {number} targetIndex
   * @param {PointerEvent} [event]
   */
  hover(targetIndex, event) {
    this.refs.slideshow?.select(targetIndex, undefined, { animate: false });
    this.refs.mobileSlideshow?.select(targetIndex, undefined, { animate: false });
    this.#updateDescriptions(targetIndex);
    if (event) this.#revealImage(event);
  }

  /**
   * Reset to active link content
   */
  resetToActive() {
    const activeIndex = this.currentIndex;
    this.refs.slideshow?.select(activeIndex, undefined, { animate: false });
    this.refs.mobileSlideshow?.select(activeIndex, undefined, { animate: false });
    this.#updateDescriptions(activeIndex);
  }

  /**
   * Update the selected link
   *
   * @param {number} index
   */
  #updateSelectedLink(index) {
    const { links } = this;
    const selectedIndex = clamp(index, 0, links.length - 1);

    for (const [idx, link] of links.entries()) {
      const isSelected = idx === selectedIndex;
      link.setAttribute('aria-current', isSelected.toString());
      
      // Add href only to active element for navigation
      if (isSelected) {
        const href = this.#getCollectionUrl(idx);
        if (href) {
          link.setAttribute('href', href);
        }
      } else {
        link.removeAttribute('href');
      }
    }
  }

  /**
   * Get collection URL by index
   *
   * @param {number} index
   * @returns {string|null}
   */
  #getCollectionUrl(index) {
    const link = this.links[index];
    if (!link) return null;
    
    // Get URL from data-collection-url attribute
    return link.dataset.collectionUrl || null;
  }

  /**
   * Update collection descriptions visibility
   *
   * @param {number} index
   */
  #updateDescriptions(index) {
    const descriptions = this.querySelectorAll('.collection-description__item');
    const selectedIndex = clamp(index, 0, descriptions.length - 1);

    for (const [idx, description] of descriptions.entries()) {
      if (description instanceof HTMLElement) {
        if (idx === selectedIndex) {
          description.style.display = 'block';
        } else {
          description.style.display = 'none';
        }
      }
    }
  }

  /**
   * Handle slideshow select event
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = async (event) => {
    if (!event.detail.userInitiated) return;

    const { index } = event.detail;
    if (index === this.currentIndex) return;

    const selectedLink = this.links[index];
    if (!selectedLink) return;

    this.#updateSelectedLink(index);

    this.#scroll.to(selectedLink);
  };

  /**
   * Cycle focus to the next or previous link
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    let modifier = 0;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        modifier = 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        modifier = -1;
        break;
    }

    if (!modifier) return;

    event.preventDefault();
    cycleFocus(this.links, modifier);
  }

  /**
   * Handle click events on collection links
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const link = target.closest('.collection-links__link');
    if (!(link instanceof HTMLElement)) return;

    const index = this.links.indexOf(link);
    if (index === -1) return;

    // If clicking on already active element and it has href, allow navigation
    if (index === this.currentIndex && link.hasAttribute('href')) {
      // Don't prevent default - let the browser navigate
      return;
    }

    // For non-active elements or active without href, prevent navigation and update state
    event.preventDefault();
    this.select(index);
  };

  /**
   * Handle scroll event
   */
  #handleScroll = () => {
    const { links } = this;
    const { container } = this.refs;
    const visibleLinks = getVisibleElements(this, links, 0.1);

    if (visibleLinks.length === 0) return;
    const centers = visibleLinks.map((link) => center(link, 'x'));
    const referencePoint = center(container, 'x');
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleLink = visibleLinks[centers.indexOf(closestCenter)];

    if (!closestVisibleLink) return;

    const index = links.indexOf(closestVisibleLink);

    this.select(index);
  };

  /**
   * Reveal an image
   *
   * @param {Event} event
   */
  #revealImage(event) {
    if (!(event instanceof PointerEvent)) return;
    if (event.pointerType === 'touch') return;

    const { target } = event;
    if (!(target instanceof HTMLElement)) return;

    const { images } = this.refs;
    const index = this.links.indexOf(target);
    const selectedImage = images?.[index];

    if (!selectedImage) return;

    /** @param {PointerEvent} event */
    const updateImagePosition = (event) => {
      const imageHeight = selectedImage.offsetHeight;
      const imageWidth = selectedImage.offsetWidth;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const offset = 15;

      const wouldBeCutOff = event.clientY + imageHeight + offset > viewportHeight;
      const yPos = wouldBeCutOff ? event.clientY - imageHeight - offset : event.clientY + offset;

      const xPos = Math.min(Math.max(offset, event.clientX + offset), viewportWidth - imageWidth - offset);

      selectedImage.style.setProperty('--x', `${xPos}px`);
      selectedImage.style.setProperty('--y', `${yPos}px`);
    };

    const reset = () => {
      selectedImage.removeAttribute('reveal');
      target.removeEventListener('mousemove', updateImagePosition);
    };

    updateImagePosition(event);

    for (const image of images) {
      if (image === selectedImage) {
        image.setAttribute('reveal', '');
      } else {
        image.removeAttribute('reveal');
      }
    }

    target.addEventListener('mousemove', updateImagePosition);
    target.addEventListener('mouseleave', reset, { once: true });
  }
}

if (!customElements.get('collection-links-component')) {
  customElements.define('collection-links-component', CollectionLinks);
}
